import { AfterViewChecked, Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { SessionService } from 'src/app/services/session-service';
import { RegistrationService } from 'src/app/services/registration-service';
import { UserService } from 'src/app/services/user-service';
import { UserDetails } from 'src/app/models/userDetails';
import {
  AccountSummary,
  AccountV2Service,
  BriefingResponse,
  FollowedPlate,
  SavedPlate,
  PlateViewEvent,
  UserInterest,
} from 'src/app/services/account-v2-service';
import {
  CollectionCard,
  EditorialFeature,
  HERO_TRUST_STATS,
  STATIC_COLLECTION_OF_WEEK,
  STATIC_CURATOR_PICKS,
  STATIC_IN_FOCUS,
} from 'src/app/services/home-v2-service';

/**
 * v2 homepage shell — ONE route, composition switched by authenticated
 * state (docs/my-market: Home_page_10 public / Home_page_12 member).
 * Shared sections (In Focus, Market, Registry, Close) are the same
 * components and data contracts in both states; only the hero/briefing
 * and the member-only modules differ. Dev-gated in app.module.ts via the
 * same isDevOnly swap as /account: production keeps LandingComponent.
 *
 * Deliberately absent until their phases land (frozen structure intact,
 * no manufactured content): For You (Phase 5 interests), auction fixture
 * (Phase 4), interests line (Phase 5), market notes (Phase 6).
 */
@Component({
  selector: 'pla-home-v2',
  templateUrl: './home-v2.component.html',
  styleUrls: ['./home-v2.component.scss'],
})
export class HomeV2Component implements OnInit, OnDestroy, AfterViewChecked {
  public member = false;

  // LIVE public data
  public plateCount: number | null = null;

  // LIVE member data
  public summary: AccountSummary | null = null;
  public briefing: BriefingResponse | null = null;
  public followed: FollowedPlate[] | null = null;
  public saved: SavedPlate[] | null = null;
  public recent: PlateViewEvent[] | null = null;
  public firstName: string | null = null;
  /** Declared interests, for the briefing summary line (the editable
      control lives in the For You module — this line links to it). */
  public interests: UserInterest[] = [];
  /** False until GET interests settles; the line keeps its height meanwhile
      so the search box below doesn't jump while someone is clicking it. */
  public interestsLoaded = false;
  public briefingFailed = false;
  /** Each briefing item's link split into path + query (routerLink takes no query string). */
  public briefingLinks: { path: string; query: { [k: string]: string } | null }[] = [];
  /** POST briefing/seen exactly once, and only after a successful render. */
  private briefingSeenPosted = false;

  // STATIC editorial + verified hero stats (see home-v2-service provenance)
  public stats = HERO_TRUST_STATS;
  public collectionOfWeek = STATIC_COLLECTION_OF_WEEK;
  public curatorPicks: CollectionCard[] = STATIC_CURATOR_PICKS;
  /** The In Focus feature, also surfaced as the briefing-visual thumbnail. */
  public inFocus: EditorialFeature = STATIC_IN_FOCUS;

  public today = new Date();

  private sessionSub: Subscription | null = null;

  constructor(
    private sessionService: SessionService,
    private registrationService: RegistrationService,
    private userService: UserService,
    private accountService: AccountV2Service
  ) {}

  ngOnInit(): void {
    this.compose();
    this.sessionSub = this.sessionService.sessionStateChange.subscribe(() =>
      this.compose()
    );
  }

  ngOnDestroy(): void {
    this.sessionSub?.unsubscribe();
  }

  /**
   * The seen contract: the anchor advances only after the member briefing
   * has RENDERED successfully. This hook runs after Angular has flushed
   * the view, so a failed GET (briefingFailed) or a prefetch that never
   * displayed can never mark the visit.
   */
  ngAfterViewChecked(): void {
    if (
      this.member &&
      this.briefing != null &&
      !this.briefingFailed &&
      !this.briefingSeenPosted
    ) {
      this.briefingSeenPosted = true;
      this.accountService.markBriefingSeen().subscribe({
        next: () => {},
        error: () => {},
      });
    }
  }

  private compose(): void {
    // LAUNCH: every visitor sees the public homepage. The personalised My
    // Market layer (below, *ngIf="member") is fully retained but held back for
    // now — restore it by setting this back to this.sessionService.isLoggedIn().
    // Login, registration and account pages are unaffected: this is a
    // presentation switch only, not an auth or permissions change.
    this.member = false;
    this.registrationService.getPlateCount().subscribe({
      // A zero count means the stats source is empty/stale (seen on dev2),
      // never a truthful market fact; hide the stat rather than render it.
      next: (res) =>
        (this.plateCount =
          res?.totalCount && res.totalCount > 0 ? res.totalCount : null),
      error: () => (this.plateCount = null),
    });
    if (this.member) {
      this.loadMember();
    }
  }

  private loadMember(): void {
    this.userService.getNullableUserDetails((details: UserDetails) => {
      this.firstName = details?.firstName || null;
    });
    this.accountService.getSummary().subscribe({
      next: (summary) => (this.summary = summary),
      error: () => {},
    });
    // Server-composed briefing (Phase 3). Reading never marks the visit;
    // ngAfterViewChecked posts briefing/seen only once the data has
    // actually rendered.
    this.accountService.getBriefing().subscribe({
      next: (briefing) => {
        this.briefing = briefing;
        this.briefingLinks = (briefing?.items || []).map((i) => this.splitLink(i.targetUrl));
      },
      error: () => (this.briefingFailed = true),
    });
    this.accountService.getFollowed().subscribe({
      next: (followed) => (this.followed = followed),
      error: () => (this.followed = []),
    });
    this.accountService.getSaved().subscribe({
      next: (saved) => (this.saved = saved),
      error: () => (this.saved = []),
    });
    this.accountService.getRecent(6).subscribe({
      next: (recent) => (this.recent = recent),
      error: () => (this.recent = []),
    });
    this.accountService.getInterests().subscribe({
      next: (interests) => {
        this.interests = interests;
        this.interestsLoaded = true;
      },
      error: () => {
        this.interests = [];
        this.interestsLoaded = true;
      },
    });
  }

  /**
   * Human summary of declared interests for the briefing lead, e.g.
   * "AAR initials · number 17". Deterministic from the structured interest
   * type — no free text. Empty string when there are none (line hidden).
   */
  public get interestsSummary(): string {
    return this.interests
      .map((i) => {
        switch (i.type) {
          case 'initials':
            return `${i.value} initials`;
          case 'number':
            return `number ${i.value}`;
          case 'collection':
            return i.value.replace(/_/g, ' ');
          default:
            return i.value;
        }
      })
      .join(' · ');
  }

  private splitLink(url: string): { path: string; query: { [k: string]: string } | null } {
    const [path, qs] = (url || '/').split('?');
    if (!qs) return { path, query: null };
    const query: { [k: string]: string } = {};
    new URLSearchParams(qs).forEach((v, k) => (query[k] = v));
    return { path, query };
  }

  public asDate(value: string): Date {
    return new Date(value);
  }

  public get greeting(): string {
    const hour = new Date().getHours();
    const part =
      hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
    return this.firstName ? `${part}, ${this.firstName}.` : `${part}.`;
  }

  public get updatesLine(): string {
    const count =
      this.briefing?.summary?.newItemCount ?? this.summary?.newUpdates ?? 0;
    if (count === 0) return 'Nothing new since your last visit';
    if (count === 1) return 'One update since your last visit';
    return `${count} updates since your last visit`;
  }
}
